import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import Button from '../shared/Button';

const SAMPLE_DATA = 'Category A,10\nCategory B,22\nCategory C,15';

const parseRows = (raw) => {
	const rows = String(raw || '')
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean)
		.map((line) => {
			const idx = line.lastIndexOf(',');
			if (idx <= 0) return null;
			const label = line.slice(0, idx).trim();
			const value = Number(line.slice(idx + 1).trim());
			if (!label || Number.isNaN(value)) return null;
			return { label, value };
		})
		.filter(Boolean);
	return rows;
};

const InsertChartModal = ({ isOpen, onClose, onInsert }) => {
	const [chartType, setChartType] = useState('bar');
	const [title, setTitle] = useState('Sample Chart');
	const [rowsText, setRowsText] = useState(SAMPLE_DATA);
	const [caption, setCaption] = useState('');

	const parsedRows = useMemo(() => parseRows(rowsText), [rowsText]);
	const isValid = parsedRows.length >= 2;

	const handleInsert = () => {
		if (!isValid) return;
		onInsert({
			chartType,
			title: title.trim() || 'Chart',
			caption: caption.trim(),
			rows: parsedRows
		});
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[10040] flex items-center justify-center p-4">
			<div className="absolute inset-0 bg-black/60" onClick={onClose} />
			<div className="relative z-10 w-full max-w-2xl rounded-xl bg-white shadow-2xl">
				<div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
					<h2 className="text-xl font-bold text-dark-blue">Insert Chart</h2>
					<button
						type="button"
						onClick={onClose}
						className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-dark-blue"
						aria-label="Close"
					>
						<X className="h-5 w-5" />
					</button>
				</div>
				<div className="grid gap-4 px-6 py-5">
					<div className="grid gap-3 sm:grid-cols-2">
						<div>
							<label className="mb-1 block text-sm font-medium text-dark-blue">Chart type</label>
							<select
								value={chartType}
								onChange={(e) => setChartType(e.target.value)}
								className="w-full rounded-lg border border-lightGrey px-3 py-2 text-sm text-dark-blue focus:border-purple focus:outline-none"
							>
								<option value="bar">Bar</option>
								<option value="line">Line</option>
								<option value="pie">Pie</option>
							</select>
						</div>
						<div>
							<label className="mb-1 block text-sm font-medium text-dark-blue">Chart title</label>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								className="w-full rounded-lg border border-lightGrey px-3 py-2 text-sm text-dark-blue focus:border-purple focus:outline-none"
								placeholder="e.g. Survey Results"
							/>
						</div>
					</div>
					<div>
						<label className="mb-1 block text-sm font-medium text-dark-blue">
							Data (one row per line: label,value)
						</label>
						<textarea
							value={rowsText}
							onChange={(e) => setRowsText(e.target.value)}
							className="h-40 w-full rounded-lg border border-lightGrey px-3 py-2 text-sm text-dark-blue focus:border-purple focus:outline-none"
							placeholder={'Category A,10\nCategory B,20'}
						/>
						<p className="mt-1 text-xs text-gray-500">
							Example: <code>Introduction,12</code>
						</p>
					</div>
					<div>
						<label className="mb-1 block text-sm font-medium text-dark-blue">
							Figure caption (optional)
						</label>
						<input
							type="text"
							value={caption}
							onChange={(e) => setCaption(e.target.value)}
							className="w-full rounded-lg border border-lightGrey px-3 py-2 text-sm text-dark-blue focus:border-purple focus:outline-none"
							placeholder="e.g. Figure showing monthly trend"
						/>
					</div>
					{!isValid && (
						<p className="text-sm text-textRed">
							Add at least two valid data rows to insert a chart.
						</p>
					)}
				</div>
				<div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
					<Button variant="outline-gradient-text" onClick={onClose} className="w-auto px-5 py-2">
						Cancel
					</Button>
					<Button onClick={handleInsert} disabled={!isValid} className="w-auto px-5 py-2">
						Insert Chart
					</Button>
				</div>
			</div>
		</div>
	);
};

export default InsertChartModal;

